import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, User } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { EntityPickerBase } from '../../../../shared/components/common';
import { useContacts } from '../hooks/useContacts';
import { ContactQuickCreateDialog } from './ContactQuickCreateDialog';
import { getEntityUrl } from '../../../../shared/utils/entityUrls';
import { getContactDisplayName } from '../utils/contactName';
import type { Contact } from '../types';

interface ContactPickerProps {
  /** Currently selected contact ID */
  value: string;
  /** Callback when a contact is selected */
  onChange: (contactId: string) => void;
  /** Callback returning the selected contact object */
  onContactSelected?: (contact: Contact | null) => void;
  /** Filter contacts by company ID */
  organizationId?: string;
  /** Optional label to display above the picker */
  label?: string;
  /** Placeholder text when no contact is selected */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the "None" option to clear selection */
  allowClear?: boolean;
  /** Whether the field is required (shows * on label) */
  required?: boolean;
  /** Default organization_id for the quick create dialog */
  defaultCompanyId?: string;
  /**
   * Pre-fill the quick-create dialog's email. For a surface that already knows the
   * address it is acting on (a portal invite to an organisation's own
   * `email_addresses` entry) but not yet the person behind it.
   */
  createDefaultEmail?: string;
  /** Hide the "Add new" action (useful in filter contexts) */
  hideAddNew?: boolean;
  /** Show selected contact as a clickable link to its detail page */
  linkToEntity?: boolean;
}

const formatContactName = (contact: Contact) =>
  getContactDisplayName(contact) || contact.email || '—';

export function ContactPicker({
  value,
  onChange,
  onContactSelected,
  organizationId,
  label,
  placeholder,
  disabled,
  className,
  allowClear = false,
  required = false,
  defaultCompanyId,
  createDefaultEmail,
  hideAddNew = false,
  linkToEntity = false,
}: ContactPickerProps) {
  const { t } = useTranslation('contacts');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createDialogName, setCreateDialogName] = useState('');

  const { data: contactsData } = useContacts({
    organization_id: organizationId || undefined,
    search: search || undefined,
    limit: 50,
  });

  const contacts = useMemo(() => contactsData?.data ?? [], [contactsData?.data]);

  const handleSelect = useCallback((contactId: string) => {
    const contact = contacts.find((c) => c.id === contactId) ?? null;
    onChange(contactId);
    onContactSelected?.(contact);
    setOpen(false);
    setSearch('');
  }, [contacts, onChange, onContactSelected]);

  const handleClear = useCallback(() => {
    onChange('');
    onContactSelected?.(null);
    setOpen(false);
    setSearch('');
  }, [onChange, onContactSelected]);

  const handleAddNew = useCallback(() => {
    setCreateDialogName(search.trim());
    setOpen(false);
    setShowCreateDialog(true);
  }, [search]);

  const handleContactCreated = useCallback((contact: Contact) => {
    onChange(contact.id);
    onContactSelected?.(contact);
    setSearch('');
    setShowCreateDialog(false);
  }, [onChange, onContactSelected]);

  const addNewLabel = search.trim()
    ? t('contactPicker.createContactWithName', { name: search.trim() })
    : t('contactPicker.addNewContact');

  return (
    <>
      <EntityPickerBase<Contact>
        value={value}
        items={contacts}
        getItemLabel={formatContactName}
        renderItem={(contact, isSelected) => (
          <>
            <Check
              className={cn('me-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
            />
            <User className="me-2 h-4 w-4 text-muted-foreground shrink-0" />
            <span className="truncate">{formatContactName(contact)}</span>
            {contact.email && (
              <span className="ms-2 text-xs text-muted-foreground shrink-0">
                {contact.email}
              </span>
            )}
          </>
        )}
        label={label}
        placeholder={placeholder ?? t('contactPicker.searchOrCreate')}
        searchPlaceholder={t('contactPicker.searchContacts')}
        emptyMessage={t('contactPicker.noContactsFound')}
        disabled={disabled}
        className={className}
        required={required}
        search={search}
        onSearchChange={setSearch}
        open={open}
        onOpenChange={setOpen}
        onSelect={handleSelect}
        allowClear={allowClear}
        onClear={handleClear}
        addNewLabel={addNewLabel}
        onAddNew={hideAddNew ? undefined : handleAddNew}
        selectedEntityUrl={linkToEntity && value
          ? getEntityUrl({ entityType: 'contacts', entityId: value })
          : undefined}
        entityIcon={linkToEntity ? User : undefined}
      />

      <ContactQuickCreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={handleContactCreated}
        skipNavigation
        defaultCompanyId={defaultCompanyId || organizationId}
        defaultFirstName={createDialogName.split(/\s+/)[0] || ''}
        defaultLastName={createDialogName.split(/\s+/).slice(1).join(' ')}
        defaultEmail={createDefaultEmail}
      />
    </>
  );
}
